import { ErrorManagementModule, IoCContainer, ISidebarManagerComponent, ISidebarModule, SettingsBase, WebCoreIoCKeys, IAssetResolver, ISidebarCssProvider } from '../..';
import { SidebarManager } from '../../pocos/sidebar-manager.class';
import { SIDEBAR_MAX_SIZE_RATIO, Sidebar, SidebarSide } from '../../pocos/sidebar.class';
import { SidebarModule } from '../../pocos/sidebar-module.class';
import { CSSHandler } from './handlers/css-handler.class';
import { MenuHandler } from './handlers/menu-handler.class';
import { MouseEventHandler } from './handlers/mouse-event-handler.class';
import { SidebarComponent } from './sidebar-component.class';

export class SidebarManagerComponent implements ISidebarManagerComponent {
    private cssHandler: CSSHandler;
    public mouseEventHandler: MouseEventHandler;

    private static SIDEBAR_STATE_EVENT = 'wecdev-sidebar-state-change';
    private static SIDEBAR_DELAY_EVENT = 'wecdev-sidebar-delay-change';
    private static SIDEBAR_REGISTRY_ATTR = 'data-sidebar-enabled';
    private static SIDEBAR_REGISTRY_ID = 'wecdev-sidebar-state-registry';
    private registry: HTMLElement;
    private globalSyncAttached: boolean = false;

    public static SHARED_MODULES_CLASS: string = 'shared-module';
    public static SHARED_MODULE_OWNER_ATTR: string = 'moduleOwner';
    public sharedModules: ISidebarModule[] = [new ErrorManagementModule()];

    public integrationModules: ISidebarModule[] = [];
    public integrationId: string;

    get shadowRoots(): ShadowRoot[] {
        let shadowRoots: ShadowRoot[] = [];
        for (let bar of this.sidebarComponents) {
            shadowRoots.push(bar.shadow);
        }
        return shadowRoots;
    }

    get settingsSidebarList(): Sidebar[] {
        if (this.settings) {
            return this.settings.sidebarManager.sidebarList;
        }
        return [];
    }

    get settingsManager(): SidebarManager {
        return this.settings.sidebarManager;
    }

    get isEnabled(): boolean {
        return this.settings.sidebarManager.isEnabled;
    }

    get sidebarLocked(): boolean {
        return !this.isEnabled || !this.settingsSidebarList || this.settingsSidebarList.length === 0;
    }

    get assetResolver(): IAssetResolver {
        return IoCContainer.get(WebCoreIoCKeys.IAssetResolver);
    }

    get baseCssFiles(): string[] {
        let cssProvider: ISidebarCssProvider = IoCContainer.get(WebCoreIoCKeys.ISidebarCssProvider);
        return cssProvider.getBaseCssFiles();
    }

    private settings: any;
    private settingsBase: SettingsBase;

    private sidebarComponents: SidebarComponent[] = [];

    constructor() {
        this.settings = IoCContainer.get(WebCoreIoCKeys.IWebCoreSettings);
        this.settingsBase = IoCContainer.get(WebCoreIoCKeys.ISettingsStorage);

        this.mouseEventHandler = new MouseEventHandler(this.sidebarComponents);
        this.cssHandler = new CSSHandler();
    }

    public updateSettings(): void {
        this.settingsBase.updateSettingsInStorage();
    }

    public searchShadowRoots(querySelector: string): Element[] {
        let results: Element[] = [];

        for (let root of this.shadowRoots) {
            let elems = Array.from(root.querySelectorAll(querySelector));
            results.push(...elems);
        }

        return results;
    }

    public reinitSidebars(): void {
        document.querySelectorAll('.wecdev-shadow-wrapper').forEach(el => el.remove());

        let enabled = this.isEnabled;
        this.registry.setAttribute(SidebarManagerComponent.SIDEBAR_REGISTRY_ATTR, String(enabled));

        document.dispatchEvent(
            new CustomEvent(SidebarManagerComponent.SIDEBAR_STATE_EVENT, {
                detail: { enabled },
            })
        );

        this.removeModules();
        this.showSidebars();
    }

    /* 2 esetben kell meghívni:
        1. sidebar dockolása után
        2. modulok áthelyezése után
    */
    public reinitModules(): void {
        this.removeModules();
        this.drawSidebarModules();
    }

    public shouldSkipSharedModule(moduleClass: new () => ISidebarModule): void {
        this.sharedModules = this.sharedModules.filter(module => !(module instanceof moduleClass));
    }

    public removeModules(): void {
        if (this.integrationId) {
            let results = this.searchShadowRoots(`.${this.integrationId}`);
            for (let moduleElement of results) {
                moduleElement.remove();
            }
        }

        if (this.sharedModules.length > 0) {
            let results = this.searchShadowRoots(`.${SidebarManagerComponent.SHARED_MODULES_CLASS}`) as HTMLElement[];
            for (let moduleElement of results) {
                if (moduleElement.dataset[SidebarManagerComponent.SHARED_MODULE_OWNER_ATTR] === 'true') {
                    moduleElement.remove();
                }
            }
        }

        for (let extensionModule of this.integrationModules) {
            extensionModule.isDrawn = false;
        }

        for (let sharedModule of this.sharedModules) {
            sharedModule.isDrawn = false;
        }
    }

    public showSidebars(): void {
        this.ensureSidebarStateRegistry();

        if (this.sidebarLocked) {
            return;
        }

        for (let i = 0; i < this.settingsSidebarList.length; i++) {
            let bar = new SidebarComponent(this, this.settingsSidebarList[i], i);
            bar.showBar();

            this.cssHandler.setStyle(bar.shadow);
            this.sidebarComponents[i] = bar;
        }

        this.initMouseEvents();
        this.ensureSidebarModules();
    }

    public ensureSidebarModules(): void {
        if (this.integrationModules.length === 0 && this.sharedModules.length === 0) {
            return;
        }

        for (let module of this.integrationModules) {
            this.setupSidebarModule(module);
        }

        for (let module of this.sharedModules) {
            if (!this.isSharedModuleInDom(module.name)) {
                this.setupSidebarModule(module);
            }
        }

        this.updateSettings();
        this.drawSidebarModules();
    }

    private isSharedModuleInDom(moduleName: string): boolean {
        let wrappers = this.searchShadowRoots(`.${SidebarManagerComponent.SHARED_MODULES_CLASS}`) as HTMLElement[];
        return wrappers.some(wrapper => wrapper.querySelector('legend')?.textContent === moduleName);
    }

    private ensureSidebarStateRegistry(): void {
        this.registry = document.getElementById(SidebarManagerComponent.SIDEBAR_REGISTRY_ID);
        if (!this.registry) {
            this.registry = document.createElement('div');
            this.registry.id = SidebarManagerComponent.SIDEBAR_REGISTRY_ID;
            document.body.appendChild(this.registry);
        }

        if (this.globalSyncAttached) {
            return;
        }
        this.globalSyncAttached = true;

        document.addEventListener(SidebarManagerComponent.SIDEBAR_STATE_EVENT, (event: Event) => {
            let custom = event as CustomEvent;
            let isEnabled = custom.detail.enabled;
            if (this.settings.sidebarManager.isEnabled === isEnabled) {
                return;
            }

            this.settings.sidebarManager.isEnabled = isEnabled;
            this.updateSettings();

            if (isEnabled) {
                this.sidebarComponents.length = 0;
                this.showSidebars();
            } else {
                this.removeModules();
                this.sidebarComponents.length = 0;
            }
        });
    }

    private setupSidebarModule(module: ISidebarModule): void {
        let found = false;

        for (let sidebar of this.settingsSidebarList) {
            if (sidebar.sidebarModuleList.some(m => m.name === module.name)) {
                found = true;
                break;
            }
        }

        if (!found) {
            let { x, y } = this.getRandomCoordsForSidebar(this.settingsSidebarList[0]);
            let newModule = new SidebarModule();
            newModule.name = module.name;
            newModule.x = x;
            newModule.y = y;

            this.settingsSidebarList[0].sidebarModuleList.push(newModule);
        }
    }

    public dockTo(newSide: SidebarSide, oldSide: SidebarSide): void {
        let index = this.findSidebarIndexBySide(oldSide);
        let existingSideItem = this.settingsSidebarList.find(item => item.orientation === newSide);

        if (existingSideItem) {
            return;
        }

        if (index !== -1) {
            this.settingsSidebarList[index].orientation = newSide;
            this.clampSizeToMax(this.settingsSidebarList[index]);
            this.updateSettings();
        }

        this.reinitModules();
    }

    private clampSizeToMax(sidebar: Sidebar): void {
        let isVertical = sidebar.orientation === SidebarSide.Left || sidebar.orientation === SidebarSide.Right;
        let maxSize = (isVertical ? window.innerWidth : window.innerHeight) * SIDEBAR_MAX_SIZE_RATIO;

        if (sidebar.size > maxSize) {
            sidebar.size = maxSize;
        }
    }

    public resize(side: SidebarSide, size: number): void {
        let index = this.findSidebarIndexBySide(side);
        if (index !== -1) {
            this.settingsSidebarList[index].size = size;
            this.updateSettings();
        }
    }

    public setModulePosition(sidebarSide: SidebarSide, moduleName: string, x: number, y: number): void {
        let index = this.findSidebarIndexBySide(sidebarSide);
        if (index !== -1) {
            for (let module of this.settingsSidebarList[index].sidebarModuleList) {
                if (module.name === moduleName) {
                    module.x = x;
                    module.y = y;
                }
            }

            this.updateSettings();
        }
    }

    private findSidebarComponentIndexBySide(side: SidebarSide): number {
        return this.sidebarComponents.findIndex(item => item.currentSide === side);
    }

    private findSidebarIndexBySide(side: SidebarSide): number {
        return this.settingsSidebarList.findIndex(item => item.orientation === side);
    }

    private initMouseEvents(): void {
        this.mouseEventHandler.init();
    }

    private getRandomCoordsForSidebar(sidebar: Sidebar): { x: number; y: number } {
        let screenWidth = window.innerWidth;
        let screenHeight = window.innerHeight;
        let padding = 25;

        let x = 0;
        let y = 0;

        switch (sidebar.orientation) {
            case SidebarSide.Left:
                x = padding + Math.random() * (sidebar.size - padding * 2);
                y = padding + Math.random() * (screenHeight - padding * 2);
                break;
            case SidebarSide.Right:
                x = padding + Math.random() * (sidebar.size - padding * 2);
                y = padding + Math.random() * (screenHeight - padding * 2);
                break;
            case SidebarSide.Top:
                x = padding + Math.random() * (screenWidth - padding * 2);
                y = padding + Math.random() * (sidebar.size - padding * 2);
                break;
            case SidebarSide.Bottom:
                x = padding + Math.random() * (screenWidth - padding * 2);
                y = padding + Math.random() * (sidebar.size - padding * 2);
                break;
        }

        return { x, y };
    }

    private moveModuleToSidebar(dir: string, module: SidebarModule): void {
        let targetSidebar: Sidebar = this.settingsSidebarList.find(bar => bar.orientation === dir);

        let { x, y } = this.getRandomCoordsForSidebar(targetSidebar);
        module.x = x;
        module.y = y;

        targetSidebar.sidebarModuleList.push(module);
        this.updateSettings();

        let fieldsets = this.searchShadowRoots('.wecdev-module-wrapper') as HTMLElement[];
        let fieldset = fieldsets.find(fs => fs.querySelector('legend')?.textContent === module.name) || null;

        fieldset.style.left = `${module.x}px`;
        fieldset.style.top = `${module.y}px`;

        let sidebarComponent: SidebarComponent = this.sidebarComponents.find(item => {
            return item.currentSide === dir;
        });

        sidebarComponent.controlBar.appendChild(fieldset);
        sidebarComponent.dragHandler.initModuleWrapperDraggable();
    }

    private drawSidebarModules(): void {
        for (let sidebar of this.settingsSidebarList) {
            let index = this.findSidebarComponentIndexBySide(sidebar.orientation);
            if (index === -1) {
                return;
            }

            if (sidebar.sidebarModuleList.length === 0) {
                continue;
            }

            for (let module of sidebar.sidebarModuleList) {
                let isSharedModule: boolean = false;
                let extensionModule: ISidebarModule = this.integrationModules.find(item => {
                    return item.name === module.name;
                });

                if (!extensionModule) {
                    extensionModule = this.sharedModules.find(item => {
                        return item.name === module.name;
                    });

                    isSharedModule = extensionModule ? true : false;
                }

                if (!extensionModule) {
                    continue;
                }

                if (isSharedModule && this.isSharedModuleInDom(module.name)) {
                    if (extensionModule) {
                        extensionModule.isDrawn = true;
                    }
                    continue;
                }

                let wrapper = document.createElement('fieldset');
                wrapper.className = isSharedModule ? `wecdev-module-wrapper ${SidebarManagerComponent.SHARED_MODULES_CLASS}` : `wecdev-module-wrapper ${this.integrationId}`;
                wrapper.style.left = `${module.x}px`;
                wrapper.style.top = `${module.y}px`;
                if (isSharedModule) {
                    wrapper.dataset[SidebarManagerComponent.SHARED_MODULE_OWNER_ATTR] = 'true';
                }

                let legend = document.createElement('legend');
                legend.className = 'wecdev-legend';
                legend.textContent = module.name;
                wrapper.appendChild(legend);

                extensionModule?.drawModule(wrapper);

                this.sidebarComponents[index].controlBar.appendChild(wrapper);
                this.sidebarComponents[index].dragHandler.initModuleWrapperDraggable();

                this.createSidebarMenu(wrapper, sidebar, module, isSharedModule);
            }
        }
    }

    private createSidebarMenu(wrapper: HTMLElement, sidebar: Sidebar, module: SidebarModule, isSharedModule: boolean): void {
        let index = this.findSidebarComponentIndexBySide(sidebar.orientation);
        if (index === -1) {
            return;
        }

        this.sidebarComponents[index].menuHandler = new MenuHandler(wrapper);

        let extensionModule: ISidebarModule;
        if (isSharedModule) {
            extensionModule = this.sharedModules.find(item => item.name === module.name);
        } else {
            extensionModule = this.integrationModules.find(item => item.name === module.name);
        }

        if (!extensionModule) {
            return;
        }

        if (extensionModule.addMenuButtons) {
            this.sidebarComponents[index].menuHandler.addModuleButtons(extensionModule.addMenuButtons());
        }

        if (this.settingsSidebarList.length > 1) {
            let arrowsGroup = this.sidebarComponents[index].menuHandler.addGroup('Move', 'menu-group-move', 'fa-solid fa-arrows-up-down-left-right');
            let arrows = [
                { dir: 'left', title: 'Left', icon: 'arrow fa-solid fa-circle-arrow-left' },
                { dir: 'top', title: 'Top', icon: 'arrow fa-solid fa-circle-arrow-up' },
                { dir: 'bottom', title: 'Bottom', icon: 'arrow fa-solid fa-circle-arrow-down' },
                { dir: 'right', title: 'Right', icon: 'arrow fa-solid fa-circle-arrow-right' },
            ];

            arrows.forEach(arrow => {
                let exists = this.settingsSidebarList.some(bar => bar.orientation === arrow.dir);
                if (!exists || arrow.dir === sidebar.orientation) {
                    return;
                }

                this.sidebarComponents[index].menuHandler.addItem(arrowsGroup, arrow.title, arrow.icon, () => {
                    let index = sidebar.sidebarModuleList.findIndex(module => module.name === extensionModule.name);
                    if (index !== -1) {
                        let currentSidebarIndex = this.findSidebarIndexBySide(sidebar.orientation);
                        this.settingsSidebarList[currentSidebarIndex].sidebarModuleList.splice(index, 1);
                    }

                    this.moveModuleToSidebar(arrow.dir, module);
                    this.reinitModules();
                });
            });
        }

        this.sidebarComponents[index].menuHandler.initJqueryMenu(this.sidebarComponents[index].shadow);
    }
}
